import { useState } from 'react'
import { Field, Label, Switch } from '@headlessui/react'
import { PencilIcon, ClipboardIcon } from '@heroicons/react/24/outline'
import { toast } from 'sonner'

import { updateBusinessDetails } from '../../lib/db/db'
import { BusinessVatSettings } from '../../store/slices/businessSlice.types'

export const VATSettings = ({
  businessId,
  vatSettings,
}: {
  businessId: string
  vatSettings?: BusinessVatSettings
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [vatRegistered, setVatRegistered] = useState(
    vatSettings?.vatRegistered ?? false,
  )
  const [vatNumber, setVatNumber] = useState(vatSettings?.vatNumber ?? '')
  const [error, setError] = useState<string | null>(null)

  const VAT_NUMBER_MAX_LENGTH = 11

  const validateVatNumber = (number: string): boolean => {
    const cleanNumber = number.replace(/[\s-]/g, '')
    const vatRegex = /^GB[0-9]{9}$/
    return vatRegex.test(cleanNumber)
  }

  const handleSave = async () => {
    if (vatRegistered && vatNumber && !validateVatNumber(vatNumber)) {
      setError('Please enter a valid VAT number (format: GB123456789)')
      return
    }

    const cleanVatNumber = vatNumber.replace(/[\s-]/g, '')
    await updateBusinessDetails({
      businessId,
      updates: {
        vatSettings: {
          vatRegistered,
          ...(vatRegistered ? { vatNumber: cleanVatNumber } : {}),
        },
      },
    })
    setIsEditing(false)
    setError(null)
  }

  const handleCopy = async () => {
    const cleanVatNumber = vatNumber.replace(/[\s-]/g, '')
    await navigator.clipboard.writeText(cleanVatNumber)
    toast.success('VAT number copied to clipboard')
  }

  return (
    <div className="mt-8">
      <div className="flex items-center mb-6 gap-4">
        <h3 className="font-medium text-xl text-gray-900">VAT</h3>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-md hover:bg-gray-50"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit
          </button>
        )}
      </div>

      <p className="text-gray-600 mb-6">
        Enable this if your business is VAT registered. We'll automatically add
        VAT return filing deadlines to your calendar to help you stay compliant.
      </p>

      <div className="space-y-6">
        <Field className="flex items-center">
          <Switch
            checked={vatRegistered}
            onChange={setVatRegistered}
            disabled={!isEditing}
            className={`${
              vatRegistered ? 'bg-blue-600' : 'bg-gray-200'
            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              !isEditing ? 'opacity-60' : ''
            }`}
          >
            <span
              className={`${
                vatRegistered ? 'translate-x-6' : 'translate-x-1'
              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
            />
          </Switch>
          <Label className="ml-3">VAT Registered</Label>
        </Field>

        {vatRegistered && (
          <div className="space-y-2">
            <div className="text-sm text-gray-600">VAT Number (optional)</div>
            <div className="flex items-center space-x-2">
              <div className="flex-grow">
                <input
                  type="text"
                  value={vatNumber}
                  onChange={(e) => {
                    setVatNumber(e.target.value)
                    setError(null)
                  }}
                  disabled={!isEditing}
                  placeholder={isEditing ? 'GB123456789' : '-'}
                  maxLength={VAT_NUMBER_MAX_LENGTH}
                  className={`w-full px-3 py-2 border rounded-md ${
                    error ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
              </div>
              {!isEditing && vatNumber && (
                <button
                  onClick={handleCopy}
                  className="text-gray-600 hover:text-gray-700 p-2"
                  title="Copy VAT Number"
                >
                  <ClipboardIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        )}

        {isEditing && (
          <div className="flex gap-3 mt-6">
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
            >
              Save Changes
            </button>
            <button
              onClick={() => {
                setIsEditing(false)
                setVatRegistered(vatSettings?.vatRegistered ?? false)
                setVatNumber(vatSettings?.vatNumber ?? '')
                setError(null)
              }}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
